// ADO #584 — a step's Label is optional. While it is empty, or still the
// default the step was created with ("Input", "Output", "Assignment"…), the
// canvas card shows a name built from what the step does: "Read marks",
// "Show total", "Set total". Deterministic and display-only: nothing here is
// written back to the node, so a label the student typed always wins and the
// stored data stays as it was.
import { useMemo } from 'react';
import useFlowStore from '@/stores/useFlowStore';
import { isWordsOnly, useWordsOnly } from '@/utils/wordsOnly';
import {
  pathConditionC, loopConditionC, conditionMath, conditionWords,
} from '@/utils/conditionText';
import { readRepeat, knownNumber } from '@/utils/repeatModel';
import { isInputEditorV2 } from '@/utils/inputEditorVersion';
import { doStepCardText, isOldStampedLabel } from '@/utils/doStep';
import { buildKeyboardEnv } from './ExpressionKeyboard/env';

// Labels a step is created with (NODE_TYPES labels, plus the names editors
// fall back to). A label equal to one of these was never chosen by the student.
const DEFAULT_LABELS = new Set([
  'input', 'output', 'display', 'assignment', 'decision', 'loop', 'action',
  'return', 'switch', 'break', 'continue', 'call', 'subflow',
]);

const MAX = 36;
const clip = (s) => (s.length > MAX ? `${s.slice(0, MAX - 1).replace(/[\s,;]+$/, '')}…` : s);

export const isDefaultLabel = (label, configLabel, node = null) => {
  const l = (label || '').trim();
  if (!l) return true;
  if (configLabel && l === String(configLabel).trim()) return true;
  // "Use your function" v2: a label that only repeats the function's name was never chosen.
  if (node && node.type === 'subflow' && node.targetFlowName && l === String(node.targetFlowName).trim() && isInputEditorV2()) return true;
  // "Do" v2: the label the old Action editor stamps on its first save (`strcpy()`) was never chosen.
  if (node && node.type === 'action' && isInputEditorV2() && isOldStampedLabel(l, node)) return true;
  return DEFAULT_LABELS.has(l.toLowerCase());
};

// `res-1[]`, `res-1.name`, `marks[i]` → the resource's name in place of its
// id; empty `[]` / `[][]` placeholders are dropped.
const resolveName = (entry, resources) => {
  if (entry == null) return '';
  const s = String(entry);
  const m = s.match(/^([^[.]+)(.*)$/);
  if (!m) return s;
  const head = m[1];
  const res = (resources || []).find((r) => r && (r.id === head || r.name === head));
  return `${res?.name || head}${m[2]}`.replace(/\[\]/g, '');
};

const listNames = (names) => {
  const uniq = [...new Set(names.filter(Boolean))];
  if (uniq.length === 0) return '';
  if (uniq.length <= 3) return uniq.join(', ');
  return `${uniq.slice(0, 3).join(', ')}…`;
};

// A guided row may name its value by id (`res-…`): the card shows the name.
const withNames = (holder, resources) => ({
  ...holder,
  conditions: (holder.conditions || []).map((c) => (c && c.resource
    ? { ...c, resource: resolveName(c.resource, resources), resourceFullPath: c.resourceFullPath ? resolveName(c.resourceFullPath, resources) : c.resourceFullPath }
    : c)),
});

// A condition on a card: the maths form (`marks ≥ 40`, `a = b and c ≠ 0`),
// never C's ==, && or ||; in words-only mode, words ("marks is at least 40").
const shownCondition = (c, wordsOnly) => (wordsOnly ? conditionWords(c) : conditionMath(c));

// "to 9" / "to n − 1" / "down to 1": the last value a count reaches.
const countEnd = (stop, down, resources, wordsOnly) => {
  const v = String(stop.value || '').trim();
  const dir = down ? 'down to' : 'to';
  if (stop.including) return `${dir} ${shownCondition(v, wordsOnly)}`;
  const n = knownNumber(v, resources);
  if (n !== null && /^-?\d+$/.test(v)) return `${dir} ${n + (down ? 1 : -1)}`;
  if (/^[A-Za-z_]\w*$/.test(v)) return `${dir} ${v} ${down ? '+' : '−'} 1`;
  return `${dir} just ${down ? 'after' : 'before'} ${shownCondition(v, wordsOnly)}`;
};

const loopText = (node, resources, wordsOnly) => {
  const r = readRepeat(node, resources);
  if (r.kind === 'while') {
    const c = loopConditionC(node, resources);
    return c ? `Repeat while ${shownCondition(c, wordsOnly)}` : '';
  }
  if (r.kind !== 'count') return '';
  const from = `Repeat for ${r.counter} from ${shownCondition(r.start, wordsOnly)}`;
  const by = r.by && r.by !== '1' ? ` by ${r.by}` : '';
  if (r.stop.mode === 'upto') return `${from} ${countEnd(r.stop, r.down, resources, wordsOnly)}${by}`;
  if (r.stop.mode === 'while' && r.stop.c) return `${from} while ${shownCondition(r.stop.c, wordsOnly)}`;
  return from;
};

/** The name a step's content gives it, or '' when it has none yet. */
export const deriveStepLabel = (node, resources = [], { wordsOnly = isWordsOnly(), v2 = isInputEditorV2() } = {}) => {
  if (!node) return '';
  let text = '';
  switch (node.type) {
    case 'input': {
      const names = listNames((node.selectedResources || []).map((e) => resolveName(e, resources)));
      if (names) text = `Read ${names}`;
      break;
    }
    case 'output': {
      const tpl = node.outputTemplate || '';
      const names = [];
      const re = /\{([^{}:]+)(?::[^{}]*)?\}/g;
      let m;
      while ((m = re.exec(tpl)) !== null) names.push(m[1].trim());
      const list = listNames(names);
      // v2: Display, the algorithm's verb (Ram, 2026-10-29); the old editors keep Show.
      const verb = v2 ? 'Display' : 'Show';
      if (list) text = `${verb} ${list}`;
      else if (tpl.trim()) text = `${verb} "${tpl.trim()}"`;
      break;
    }
    case 'assignment': {
      const names = [];
      (node.assignments || []).forEach((a) => {
        if (!a) return;
        if (a.unpack && Array.isArray(a.targetVariables)) {
          a.targetVariables.forEach((t) => names.push(t && (t.variableName || resolveName(t.variableId, resources))));
        } else if (a.fullPath) {
          names.push(resolveName(a.fullPath, resources));
        } else if (a.targetVariable) {
          names.push(resolveName(a.targetVariable, resources));
        }
      });
      const list = listNames(names);
      if (list) text = `Set ${list}`;
      break;
    }
    case 'decision': {
      // Decision v2 (Ram, 2026-10-23): the first path's condition as maths
      // ("Check marks ≥ 40"), in words when words-only — never C's ==, &&, ||.
      const first = (node.outcomes || []).find((o) => o && !o.isDefault);
      const cond = first ? pathConditionC(withNames(first, resources), resources) : '';
      if (cond) text = wordsOnly ? `Check if ${conditionWords(cond)}` : `Check ${conditionMath(cond)}`;
      break;
    }
    case 'loop': {
      if (node.loopType === 'forEach') {
        const item = node.forEachConfig?.itemVariable;
        const coll = resolveName(node.forEachConfig?.collectionFullPath || node.forEachConfig?.collection, resources);
        if (item && coll) text = `For each ${item} in ${coll}`;
        else if (item) text = `For each ${item}`;
      } else if (node.loopType === 'for' || !node.loopType || node.loopType === 'while') {
        // Repeat v2 (Ram, 2026-10-23): "Repeat for i from 0 to 9",
        // "Repeat while count < 10" — in every mode.
        text = loopText(withNames(node, resources), resources, wordsOnly);
      }
      break;
    }
    case 'action': {
      // "Do" v2 (Ram, 2026-10-24): the call in words, "Copy "Ravi" into name",
      // "Set len to the length of name" — in every mode.
      if (v2) {
        let env;
        try { env = buildKeyboardEnv({ resources }); } catch (e) { env = undefined; }
        text = doStepCardText(node, env);
        break;
      }
      const fn = node.actionData?.functionName || node.actionData?.method || node.functionName;
      if (fn) text = `Call ${fn}`;
      break;
    }
    case 'return': {
      const v = (node.returnValue || '').trim();
      // "Give back" v2 (Ram, 2026-10-23): "Give back basic × 0.25" — the maths
      // form, in words when words-only.
      // An empty one only stops the function here.
      if (v2) text = v ? `Give back ${wordsOnly ? conditionWords(v) : conditionMath(v)}` : 'Stop here';
      else text = v ? `Return ${v}` : '';
      break;
    }
    case 'subflow': {
      // "Use your function" v2: "Use findMax", in every mode.
      if (v2) text = node.targetFlowName ? `Use ${node.targetFlowName}` : 'Use your flow';
      break;
    }
    default:
      text = '';
  }
  return text ? clip(text) : '';
};

/**
 * What a canvas card shows as the step's name: the student's own label when
 * they typed one, else a name built from the content, else the default.
 */
export const cardLabel = (node, configLabel, resources = [], opts = {}) => {
  const label = (node?.label || '').trim();
  if (label && !isDefaultLabel(label, configLabel, node)) return node.label;
  return deriveStepLabel(node, resources, opts) || node?.label || configLabel || '';
};

/** Hook form: resolves resource ids against the active flow + globals. */
export const useCardLabel = (node, configLabel) => {
  const activeFlowId = useFlowStore((s) => s.activeFlowId);
  const flows = useFlowStore((s) => s.flows);
  const globalResources = useFlowStore((s) => s.globalResources);
  const resources = useMemo(() => {
    const flow = (flows || []).find((f) => f.id === activeFlowId);
    return [...(flow?.resources || []), ...(globalResources || [])];
  }, [flows, activeFlowId, globalResources]);
  const wordsOnly = useWordsOnly();
  return cardLabel(node, configLabel, resources, { wordsOnly });
};
